
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axiosInstance from '../axiosConfig';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';
import '../styles.css';

const ArDetails = () => {
  const { id } = useParams(); 
  const [userData, setUserData] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelled, setCancelled] = useState(false);
  const [approver, setApprover] = useState({});
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [isIdCardPopupVisible, setIsIdCardPopupVisible] = useState(false);

  const currentUserId = localStorage.getItem('id');

  useEffect(() => {
    const screenWidth = window.innerWidth;
    if (screenWidth < 520) {
      setIsSidebarVisible(false);
    } else {
      setIsSidebarVisible(true);
    }
  }, []);

  const handleToggleClick = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  useEffect(() => {
    if (currentUserId) {
      axiosInstance.get(`/user/${currentUserId}/`)
        .then((response) => {
          setUserData(response.data);
          return axiosInstance.get(`/get-ar-request/${id}/`);
        })
        .then((response) => {
          setAttendanceData(response.data); 
          setLoading(false); 
        })
        .catch((error) => {
          console.error('Error fetching data:', error);
          setLoading(false);
        });
    }
  }, [currentUserId, id]); 
  
  useEffect(() => {
    axiosInstance.get(`/assign-manager/${currentUserId}/`)
      .then((response) => setApprover(response.data))
      .catch((error) => console.error('Error fetching approver data:', error));
  }, [currentUserId]);

  const handleCancelRequest = () => {
    axiosInstance.post(`/cancel-ar-request/${id}/`)
      .then((response) => {
        console.log('Attendance request cancelled successfully');
        setCancelled(true); 
        setAttendanceData({ ...attendanceData, status: 'cancelled' });
      })
      .catch((error) => console.error('Error cancelling attendance request:', error));
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  const menuItems = [
    { icon: '🏠', label: 'Dashboard', path: '/user-dashboard' },
    { icon: '👤', label: 'My Profile', path: `/user-profile/${userData?.id}` },
    { icon: '📅', label: 'Attendance', path: `/leave-attendance/${userData?.id}` },
    { icon: '📋', label: 'Leave Management', path: `/leave-management/${userData?.id}` },
    // { icon: '📅', label: 'Calendar', path: '/calendar' },
    { icon: '📝', label: 'AR Request', path: `/ar-request/${userData?.id}` },
    { icon: '🗓', label: 'My Calendar', path: '/my-calendar' },
    { icon: '📋', label: 'AR Transactions', path: '/ar-transaction/' },
    { icon: '🆔', label: 'ID Card', action: () => setIsIdCardPopupVisible(true) },
  ];

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'status-badge-approved';
      case 'pending':
        return 'status-badge-pending';
      case 'rejected':
        return 'status-badge-rejected';
      case 'cancelled':
        return 'status-badge-cancelled';
      default:
        return 'status-badge-default';
    }
  };

  const profilePhotoUrl = userData?.profile_photo?.startsWith('http://') 
    ? userData?.profile_photo.replace('http://', 'https://') 
    : userData?.profile_photo;

  if (loading) {
    return (
      <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
        <Header 
          onToggleClick={handleToggleClick} 
          isSidebarVisible={isSidebarVisible} 
        />
        
        <div className="dashboard-main-content">
          <div className="ar-details-loading">
            <div className="loading-spinner"></div>
            <p>Loading attendance request details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Header 
        onToggleClick={handleToggleClick} 
        isSidebarVisible={isSidebarVisible} 
      />
      
      <Sidebar 
        isSidebarVisible={isSidebarVisible}
        currentUser={userData}
        menuItems={menuItems}
        handleLogout={handleLogout}
      />
      
      <div className="dashboard-main-content">
        <div className="ar-details-container">
          <div className="ar-details-back">
            <Link to="/ar-transaction/" className="back-link">
              <span>←</span> Back to AR Transactions
            </Link>
          </div>

          <div className="ar-details-card">
            <div className="ar-details-header">
              <div className="header-icon">📋</div>
              <div className="header-content">
                <h1>Attendance Request Details</h1>
                <p>Created on {attendanceData ? new Date(attendanceData.created_at).toLocaleString() : 'N/A'}</p>
              </div>
              {attendanceData && (
                <span className={`status-badge ${getStatusBadgeClass(attendanceData.status)}`}>
                  {attendanceData.status}
                </span>
              )}
            </div>

            {attendanceData ? (
              <div className="ar-details-body">
                <div className="ar-details-grid">
                  <div className="detail-section">
                    <h3><span className="section-icon">⏰</span> Time Details</h3>
                    <div className="detail-row">
                      <span className="detail-label">In Date</span>
                      <span className="detail-value in-value">{attendanceData.in_date}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">In Time</span>
                      <span className="detail-value in-value">{attendanceData.in_time}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Out Date</span>
                      <span className="detail-value out-value">{attendanceData.out_date}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Out Time</span>
                      <span className="detail-value out-value">{attendanceData.out_time}</span>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h3><span className="section-icon">📝</span> Request Information</h3>
                    <div className="detail-row full-width">
                      <span className="detail-label">Reason</span>
                      <span className="detail-value reason-value">{attendanceData.reason}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Approved On</span>
                      <span className="detail-value">{new Date(attendanceData.updated_at).toLocaleString()}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Status</span>
                      <span className={`detail-value ${getStatusBadgeClass(attendanceData.status)}`}>
                        {attendanceData.status}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="approver-section">
                  <h3><span className="section-icon">👤</span> Approver Information</h3>
                  <div className="approver-card">
                    <div className="approver-avatar">👤</div>
                    <div className="approver-info">
                      <span className="approver-name">{approver.manager_name || 'N/A'}</span>
                      <span className="approver-role">Manager</span>
                    </div>
                  </div>
                </div>

                <div className="ar-details-actions">
                  {cancelled ? (
                    <div className="success-message">
                      <span className="success-icon">✓</span>
                      Request Cancelled Successfully!
                    </div>
                  ) : (
                    attendanceData.status?.toLowerCase() !== 'approved' && 
                    attendanceData.status?.toLowerCase() !== 'rejected' &&
                    attendanceData.status?.toLowerCase() !== 'cancelled' &&
                    attendanceData.status?.toLowerCase() !== 'approved' && (
                      <button 
                        className="cancel-request-btn"
                        onClick={handleCancelRequest}
                      >
                        <span>✕</span> Cancel Request
                      </button>
                    )
                  )}
                </div>
              </div>
            ) : (
              <div className="ar-details-empty">
                <div className="empty-icon">📋</div>
                <h3>No Attendance Request Found</h3>
                <p>The attendance request you're looking for doesn't exist.</p>
                <Link to="/ar-transaction/" className="action-link">
                  Go to AR Transactions
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* ID Card Popup */}
      {isIdCardPopupVisible && (
        <div className="id-card-overlay" onClick={() => setIsIdCardPopupVisible(false)}>
          <div className="id-card-popup" onClick={(e) => e.stopPropagation()}>
            <button className="id-card-close" onClick={() => setIsIdCardPopupVisible(false)}>×</button>
            
            <div className="id-card-header">
              <img src="/Teamhalt.png" alt="Logo" className="id-card-logo" />
              <span>TEAMHALT PRIVATE LIMITED</span>
            </div>
            
            <div className="id-card-body">
              <div className="Id-card-profile">
                <img
                  src={profilePhotoUrl || '/profile.jpg'}
                  alt="Profile"
                  className="id-card-photo"
                />
                <div className="id-card-info">
                  <h4>{userData?.first_name} {userData?.last_name}</h4>
                  <p className="id-designation">{userData?.designation}</p>
                  <div className="id-dob">
                    <strong>DOB:</strong>
                    <span>
                      {userData?.date_of_birth
                        ? new Date(userData.date_of_birth).toLocaleDateString("en-GB").replace(/\//g, "-")
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>
              
              <hr className="id-card-divider" />
              
              <div className="Id-card-details">
                {[
                  { label: "Emp ID", value: userData?.employee_code },
                  { label: "Department", value: userData?.department },
                  { label: "ESIC No.", value: userData?.esic_no },
                  { label: "UAN No.", value: userData?.uan_no },
                  { label: "Gender", value: userData?.gender },
                  { label: "Contact No.", value: userData?.contact_number },
                ].map((item, idx) => (
                  <div key={idx} className="detail-row">
                    <strong>{item.label}:</strong>
                    <span>{item.value || "N/A"}</span>
                  </div>
                ))}
              </div>
              
              {typeof userData?.work_location_name === "string" &&
                userData.work_location_name.toLowerCase() !== "noida" && (
                <div className="Id-card-duty">
                  <strong>On Contractual Duty Of:</strong>
                  <span>{userData?.duty_of || "N/A"}</span>
                </div>
              )}
              
              <div className="id-card-footer">
                https://teamhalt.com/
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArDetails;


